<script>
   /**************************************************************/
   /* SIMCASettings                                              */
   /* -------------------------------------                      */
   /* shows panel with SIMCA model settings.                     */
   /**************************************************************/

   import { untrack } from 'svelte';
   import { Select, Container, Switch } from 'svelte-controls-basic';
   import { NIMCA } from '../utils.svelte.js';

   let {
      calset,                    // JSON with calibration set
      isAlt = false,             // logical, is the widget shown for alternative classes only
      fit = true,                // logical, is widget used for model fitting or not
      nimca = $bindable(),       // object with NIMCA model
      center = $bindable(true),  // logical, center the data before fitting
      scale = $bindable(false),  // logical, scale the data before fitting
      alpha = $bindable(5),      // alpha, gamma and limits type for the next fit
      gamma = $bindable(1),
      limType = $bindable('classic'),
   } = $props();



   // possible choices for alpha and gamma
   const alphaOptions = [10, 5, 1, 0.1];
   const gammaOptions = [5, 1, 0.1, 0.01];

   // 'Container' hands the id of its label down to the control it wraps, which is how the control
   // gets an accessible name. without it every selector here was announced only by its value
   const uid = $props.id();

   // message from a failed fit. it is rendered outside the block below, which needs a model to
   // show anything, so that a failure with no model yet is still visible
   let fiterr = $state('');

   // builds a new model for the data currently on screen, with the settings the user picked. they
   // are props and not read from the previous model: the Train tab drops its model before a refit
   // after an exclusion or a change of preprocessing, so there is often no previous model to read
   function refit() {
      if (!fit || !calset) return;

      const previous = nimca;

      // is this a new model for the set the current one already describes, or a replacement for a
      // model which belongs to different data? only the first may keep the old model when the fit
      // fails - keeping it in the second case lets the tab go on to predict this set with a model
      // built from another, and offer that result for download
      const sameSet = previous ? previous.calsetToken === (calset.nimcaCalsetToken ?? null) : false;

      try {
         nimca = NIMCA.fit(calset, center, scale, {alpha, gamma, limType});
         // '?? null' so that a set which has somehow not been stamped yet leaves the model looking
         // like a legacy one, which the branch below handles, rather than pinning it to 'undefined'
         nimca.calsetToken = calset.nimcaCalsetToken ?? null;
         fiterr = '';
      } catch (e) {
         // 'fiterr' is only ever cleared by a fit which succeeded - a skipped fit is not one
         fiterr = e instanceof Error ? e.message : String(e);

         if (sameSet) {
            // the controls go back to the settings of the model still in place, so what is on
            // screen is what is actually being used
            center = previous.center;
            scale = previous.scale;
         } else {
            // this runs the effect below once more, which tries the fit again and fails again;
            // the second failure assigns null over null and stops there
            nimca = null;
         }
      }
   }

   // the model is built here only when there is not one for this calibration set already. changing
   // centering or scaling is the user's own action and goes through the switches below instead.
   // 'untrack' keeps the reads inside 'refit' - the settings and the previous model - from becoming
   // dependencies of this effect
   $effect(() => {
      if (!fit || !calset) return;

      if (!nimca) {
         untrack(refit);
         return;
      }

      // a model restored from a session written before models recorded their calibration set has
      // no token, while the set stored alongside it has just been given one. such a model is
      // adopted only while it still describes that set - otherwise the session was assembled from
      // parts and the model has to be built again. identical data swapped for other identical data
      // can not be told apart here, because those sessions carry no provenance at all
      if (nimca.calsetToken === null) {
         const fits = nimca.nrows === calset.values.nrows && nimca.ncols === calset.values.ncols &&
            nimca.className === calset.classNames[0] &&
            nimca.center === center && nimca.scale === scale;
         untrack(() => {
            if (fits) nimca.calsetToken = calset.nimcaCalsetToken; else refit();
         });
         return;
      }

      if (nimca.calsetToken !== calset.nimcaCalsetToken ||
          nimca.center !== center || nimca.scale !== scale) {
         untrack(refit);
      }
   });

</script>

{#if nimca && nimca.className}
<Container name="target-class" label="">
   <p class="title">Target class: {nimca.className}</p>
</Container>
{/if}

<!-- outside the model's block: after a failed fit on new data there is no model, and these are
     the settings which can make the next fit work -->
{#if fit && calset}
<Container name="center" label="Center" id="{uid}-center">
   <Switch bind:value={center} options={["no", "yes"]} onchange={refit} />
</Container>

<Container name="scale" label="Scale" id="{uid}-scale">
   <Switch bind:value={scale} options={["no", "yes"]} onchange={refit} />
</Container>
{/if}

{#if nimca && nimca.className}
<Container name="alpha" label='Alpha, %:' id="{uid}-alpha">
   <Select bind:value={nimca.alpha} options={alphaOptions} onchange={v => alpha = v} />
</Container>

{#if !isAlt}
<Container name="gamma" label='Gamma, %:' id="{uid}-gamma">
   <Select bind:value={nimca.gamma} options={gammaOptions} onchange={v => gamma = v} />
</Container>
{/if}

<Container name="limits" label='Limits:' id="{uid}-limits">
   <Select bind:value={nimca.limType} options={['classic', 'robust']} onchange={v => limType = v} />
</Container>
{/if}

<!-- error message, outside the block above so that a first fit which failed is still reported -->
{#if fiterr}
<Container status="error">
   <p>{fiterr}</p>
</Container>
{/if}
